import type {
  ForgotPasswordResponse,
  ChangePasswordForm,
  LoginResponse,
  LoginApiResponse,
  AuthTokensResponse,
  OtpChallengeResponse,
  ResendOtpResponse,
  ProfileResponse,
  resetPasswordResponse,
  SignupResponse,
  User,
} from '@/types/auth';
import api from './client';
import type {
  GetCompaniesParams,
  GetCompaniesResponse,
  GetLinkedCompaniesResponse,
  RegisterCompanyPayload,
} from '@/types/company';
import type { ApiResponse } from '@/types/generic';

// A plain `'requiresOtp' in data.data` check only narrows the `data` property
// itself, not the enclosing response — this type guard narrows the whole
// LoginApiResponse so callers can safely branch on it.
export function isOtpChallengeResponse(
  response: LoginApiResponse
): response is OtpChallengeResponse {
  return 'requiresOtp' in response.data;
}

export async function login(
  email: string,
  password: string
): Promise<LoginApiResponse> {
  const payload = { email, password };
  const { data } = await api.post<LoginApiResponse>('/auth/login', payload, {
    headers: { portal: 'company', 'x-platform-type': 'web' },
  });
  return data;
}

export async function verifyOtp(
  otpToken: string,
  otp: string
): Promise<AuthTokensResponse> {
  const { data } = await api.post<AuthTokensResponse>(
    '/auth/verify-otp',
    { otpToken, otp },
    { headers: { portal: 'company', 'x-platform-type': 'web' } }
  );
  return data;
}

export async function resendOtp(otpToken: string): Promise<ResendOtpResponse> {
  const { data } = await api.post<ResendOtpResponse>(
    '/auth/resend-otp',
    { otpToken },
    { headers: { portal: 'company', 'x-platform-type': 'web' } }
  );
  return data;
}

export async function signup(
  firstName: string,
  lastName: string,
  email: string,
  password: string
): Promise<SignupResponse> {
  const payload = { firstName, lastName, email, password };
  const { data } = await api.post<SignupResponse>('/auth/sign-up', payload, {
    headers: { portal: 'company', 'x-platform-type': 'web' },
  });
  return data;
}

export async function getProfile() {
  const { data } = await api.get<ProfileResponse>('/users/me');
  return data;
}

export async function updateProfile(data: Partial<User>) {
  const res = await api.put<ProfileResponse>('/users/me', data);
  return res.data; // this is the User object inside data
}

export async function getCompanies(params: GetCompaniesParams) {
  const { data } = await api.get<GetCompaniesResponse>('/companies', {
    params,
  });
  return data;
}

export async function getLinkedCompanies() {
  const { data } = await api.get<GetLinkedCompaniesResponse>(
    '/auth/linked-companies'
  );
  return data;
}

export async function registerCompany(
  payload: RegisterCompanyPayload
): Promise<LoginResponse> {
  // v3: at least one complete location (country, province, city).
  const { data } = await api.post<LoginResponse>(
    '/companies/registerCompany',
    payload,
    { baseURL: '/api/v3' }
  );
  return data;
}
export async function terminateSession(sessionId: string) {
  const res = await api.put<{
    message: string;
  }>(
    `sessions/${sessionId}/terminate`,
    {}, // no body, so pass empty object
    {
      params: { sessionId }, // query params go here
    }
  );
  return res.data;
}

export async function sendForgotPasswordLink(
  email: string
): Promise<ForgotPasswordResponse> {
  const res = await api.post<ForgotPasswordResponse>(
    '/forgot-password',
    {
      email: email,
    },
    {
      headers: { portal: 'company' },
    }
  );

  return res.data;
}

export async function resetPassword(
  token: string,
  newPassword: string
): Promise<resetPasswordResponse> {
  const res = await api.post<resetPasswordResponse>('/reset-password', {
    token,
    newPassword,
  });

  return res.data;
}
export async function changePassword(
  payload: ChangePasswordForm
): Promise<ApiResponse> {
  const { data } = await api.post<SignupResponse>(
    '/auth/change-password',
    payload
  );
  return data;
}

export async function logoutUser(): Promise<{ message: string }> {
  const { data } = await api.post<{ message: string }>(
    '/auth/logout',
    {},
    { headers: { portal: 'company', 'x-platform-type': 'web' } }
  );
  return data;
}

export async function getLastSelectedCompany(): Promise<{
  lastSelectedCompanyId: string | null;
}> {
  const { data } = await api.get<
    ApiResponse<{ lastSelectedCompanyId: string | null }>
  >('/users/me/last-selected-company');
  return data.data;
}

export async function updateLastSelectedCompany(
  companyId: string
): Promise<void> {
  await api.put('/users/me/last-selected-company', { companyId });
}
